import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import { PageTitle } from "../../../_metronic/layout/core";
import { GenericTable } from "../generic/table/GenericTable";
import { defaultList } from "../generic/InitialStates";
import { ActionsColumnFormatter } from "../../column-formatters/ActionsColumnFormatter";
import { withSyncDot } from "../../column-formatters/SyncStatusColumnFormatter";
import { ENTITY_ID_RAW_PURCHASE } from "../generic/Constants";
import { formatDateTime } from "../../utils/Utils";
import { SyncProgressDialog } from "../../generic-components/SyncProgressDialog";
import { useSyncOperations } from "../../hooks/useSyncOperations";
import { useSyncEnabled } from "../../hooks/useSyncEnabled";

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

// Calculate total from itemsList
const calculateTotal = (itemsList, roundOffAmount = 0) => {
    if (!itemsList || itemsList.length === 0) return 0;

    let total = 0;
    itemsList.forEach(item => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discount = parseFloat(item.discountAmount) || 0;
        const itemSubtotal = (price * quantity) - discount;

        // Calculate taxes
        let itemTax = 0;
        if (item.taxPlans && item.taxPlans.length > 0) {
            item.taxPlans.forEach(tax => {
                const percentMatch = tax.name?.match(/\((\d+(?:\.\d+)?)\s*%\)/);
                const taxPercent = percentMatch ? parseFloat(percentMatch[1]) : 0;
                itemTax += (itemSubtotal * taxPercent) / 100;
            });
        }
        total += itemSubtotal + itemTax;
    });
    return total + (parseFloat(roundOffAmount) || 0);
};

export function PurchasesListPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);

    // Check if entity is sync-enabled and get push/pull capabilities
    const { syncEnabled, pushEnabled, pullEnabled } = useSyncEnabled(ENTITY_ID_RAW_PURCHASE);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    // Use sync operations hook with push/pull capabilities
    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        pullFromServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_RAW_PURCHASE, reloadData, { pushEnabled, pullEnabled });

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // View purchase details (read-only)
    function openViewDialog(row) {
        navigate(`/purchase-new/view/${row.id}`);
    }

    // Print GRN
    function openPrintDialog(row) {
        navigate(`/purchase-print/${row.id}`);
    }

    // Build columns dynamically based on sync enabled status
    const columns = useMemo(() => {
        const baseColumns = [
            {
                dataField: "purchaseTicketNumber",
                text: "Ticket Number",
                search: true,
                formatter: (() => {
                    const fmt = (cell, row) => (
                        <span
                            className="text-primary fw-bold cursor-pointer"
                            onClick={() => openViewDialog(row)}
                            style={{ cursor: 'pointer' }}
                        >
                            {cell || 'N/A'}
                        </span>
                    );
                    return syncEnabled ? withSyncDot(fmt) : fmt;
                })(),
                headerStyle: { width: '130px' }
            },
            {
                dataField: "supplier.name",
                text: "Supplier",
                formatter: (cell, row) => (
                    <span>{row.supplier?.name || '-'}</span>
                ),
            },
            {
                dataField: "itemsList",
                text: "Items",
                formatter: (cell) => (
                    <span className="badge bg-light-primary text-primary">
                        {cell?.length || 0} item(s)
                    </span>
                ),
                headerStyle: { width: '100px' }
            },
            {
                dataField: "totalAmount",
                text: "Total Amount",
                formatter: (cell, row) => {
                    const total = calculateTotal(row.itemsList, row.roundOffAmount);
                    return (
                        <span className="fw-semibold text-success">
                            {formatCurrency(total)}
                        </span>
                    );
                },
                headerStyle: { width: '140px' }
            },
            {
                dataField: "vehicleNumber",
                text: "Vehicle",
                formatter: (cell) => (
                    <span>{cell || '-'}</span>
                ),
                headerStyle: { width: '130px' }
            },
            {
                dataField: "purchaseDate",
                text: "Purchase Date",
                formatter: (cell) => formatDateTime(cell),
                headerStyle: { width: '180px' }
            },
        ];

        // Add actions column (View and Print only - no Edit/Delete on client)
        baseColumns.push({
            dataField: "action",
            text: "Actions",
            headerStyle: { width: "140px" },
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons: [
                    {
                        name: "View",
                        color: "info",
                        icon: "eye",
                        method: openViewDialog,
                    },
                    {
                        name: "Print",
                        color: "success",
                        icon: "print",
                        method: openPrintDialog,
                    },
                ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
        });

        return baseColumns;
    }, [syncEnabled]);

    // Add new purchase (1-step)
    function addButtonClicked() {
        navigate('/purchase-new/new');
    }

    useEffect(() => {
        if (myData && myData.setBaseData) {
            // Build toolbar buttons based on sync capabilities
            const toolBarButtons = [];

            if (syncEnabled && pushEnabled) {
                toolBarButtons.push({
                    name: "Push to Server",
                    color: "info",
                    icon: "fa fa-cloud-upload-alt",
                    onClick: pushToServer,
                });
            }

            if (syncEnabled && pullEnabled) {
                toolBarButtons.push({
                    name: "Pull from Server",
                    color: "success",
                    icon: "fa fa-cloud-download-alt",
                    onClick: pullFromServer,
                });
            }

            toolBarButtons.push({
                name: "Record 1 Step Purchase",
                color: "primary",
                icon: "fa fa-plus",
                onClick: addButtonClicked,
            });

            myData.setBaseData({ toolBarButtons });
        }

        setPageLoadComplete(true);
    }, [pushToServer, pullFromServer, syncEnabled, pushEnabled, pullEnabled]);

    const BaseCard = () => (
        <div className="card card-body">
            {/* Sync Info Banner */}
            {syncEnabled && (
                <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                    <i className="fas fa-info-circle me-3 fs-3"></i>
                    <div>
                        <strong>Data Retention Policy</strong>
                        <br />
                        <small>
                            Synced records (marked with <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',backgroundColor:'#50cd89',verticalAlign:'middle'}}></span>) will be automatically
                            removed from this device after <strong>2 days</strong>. These records are safely backed up on the server
                            and can be accessed from the main system.
                        </small>
                    </div>
                </div>
            )}

            {pageLoadComplete && (
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_RAW_PURCHASE}
                    fixedFilterColumn="isPartialRecord"
                    fixedFilterData={false}
                    sortBy="purchaseDate"
                    sortOrder="desc"
                />
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Purchases List</PageTitle>
            <BaseCard />
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
        </>
    );
}
